import React from "react";
import { Link } from "react-router-dom";
import { Globe, Building2, Sparkles, ShieldCheck, Cpu, Languages, Users, Award } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import Seo, { ORG_JSON_LD } from "../components/marketing/Seo";

const ABOUT_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  url: "https://hellosir.ai/about",
  name: "About HelloSir.ai",
  description:
    "HelloSir.ai is an AI communication platform — text chatbots, voice chatbots and AI calling agents — built by Cloud Lab Technologies, a UK software company.",
  mainEntity: ORG_JSON_LD,
};

const PILLARS = [
  { Icon: Globe,        title: "Built for every market",       desc: "20+ languages out of the box. Strict locale-locking so your agent never breaks character.", color: "#0044FF" },
  { Icon: Cpu,          title: "Best-in-class AI",             desc: "Modular pipeline — swap models without re-training. Always grounded in your knowledge base.", color: "#7C3AED" },
  { Icon: ShieldCheck,  title: "Enterprise-grade privacy",     desc: "Zero-retention model partnerships, GDPR-ready, AES-256 at rest, TLS in transit.", color: "#10B981" },
  { Icon: Languages,    title: "Train once. Deploy everywhere", desc: "One knowledge base powers chat, web voice and phone calls — no duplicate config.", color: "#F97316" },
];

const VALUES = [
  { tag: "Speed",    text: "Ship a working agent in under 10 minutes — that's our north-star metric." },
  { tag: "Honesty",  text: "Agents say 'I don't know' rather than guess. End-users always know they're talking to AI." },
  { tag: "Open",     text: "No vendor lock-in. Move between plugin, snippet or API any time." },
  { tag: "Fair",     text: "Pay-as-you-go, no surprise renewals, credits never expire." },
];

const NUMBERS = [
  { value: "20+",   label: "Languages live" },
  { value: "3",     label: "Channels — text, web voice, phone" },
  { value: "10",    label: "Industry templates included" },
  { value: "99.5%", label: "Target uptime" },
];

export default function About() {
  return (
    <MarketingLayout>
      <Seo
        title="About Us"
        description="HelloSir.ai is an AI communication platform from Cloud Lab Technologies (UK). We help businesses worldwide deploy text chatbots, voice chatbots and AI calling agents — train once, deploy everywhere."
        keywords={["about hellosir.ai", "Cloud Lab Technologies", "AI chatbot company", "UK AI startup", "AI communication platform", "AI calling agent provider"]}
        path="/about"
        jsonLd={ABOUT_LD}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0] bg-gradient-to-br from-[#F0F4FF] via-white to-[#FFF1F8]">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-50 animate-hs-blob"
             style={{ background: "radial-gradient(circle, #0044FF 0%, transparent 70%)" }} aria-hidden />
        <div className="absolute -bottom-24 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 animate-hs-blob"
             style={{ background: "radial-gradient(circle, #EC4899 0%, transparent 70%)", animationDelay: "3s" }} aria-hidden />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <span className="text-xs font-mono uppercase tracking-widest text-[#0044FF]">// about hellosir.ai</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl mt-3 leading-[0.95] tracking-tight">
            We build the AI <br />
            <span className="bg-gradient-to-r from-[#0044FF] via-[#7C3AED] to-[#EC4899] bg-clip-text text-transparent">your front line deserves.</span>
          </h1>
          <p className="text-lg text-[#4B5563] mt-8 max-w-3xl">
            HelloSir.ai is a worldwide communication platform that lets any business — from a single-clinic dentist to a global eCommerce brand — deploy
            multilingual <strong>text chatbots</strong>, <strong>voice chatbots</strong> and <strong>AI calling agents</strong> trained on their own
            knowledge. One brain, three channels, every language your customers speak.
          </p>
        </div>
      </section>

      {/* Cloud Lab parent */}
      <section className="py-16 lg:py-20 border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// our parent</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">
              A product of <span className="text-[#0044FF]">Cloud Lab Technologies</span>.
            </h2>
            <p className="text-[#4B5563] mt-5 leading-relaxed">
              HelloSir.ai is built and operated by{" "}
              <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer" className="text-[#0044FF] hover:underline font-semibold">
                Cloud Lab Technologies
              </a>
              , a software technology company headquartered in the United Kingdom. Cloud Lab ships production software for clients across the UK, Europe,
              the Middle East, North America and Asia — applying that same engineering discipline to bring enterprise-grade conversational AI to every
              business, regardless of size.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer" className="btn-primary text-sm" data-testid="cloudlab-link">
                Visit cloudlabweb.com
              </a>
              <Link to="/contact" className="btn-outline text-sm">Talk to the team</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="p-8 rounded-[4px] bg-gradient-to-br from-[#0F1115] to-[#1E293B] text-white border border-[#030712] shadow-xl">
              <Building2 className="w-8 h-8 text-[#10B981]" />
              <div className="font-display font-extrabold text-2xl mt-6 text-white">Cloud Lab Technologies</div>
              <div className="text-sm font-mono text-white/70 mt-1">United Kingdom · Software Technology</div>
              <ul className="text-sm text-white/85 mt-6 space-y-2.5">
                <li className="flex items-start gap-2.5"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#10B981] shrink-0" /> Founded with a focus on practical AI for business</li>
                <li className="flex items-start gap-2.5"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#0044FF] shrink-0" /> Multi-region delivery &amp; 24/7 monitoring</li>
                <li className="flex items-start gap-2.5"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#EC4899] shrink-0" /> Full-stack: web, mobile, AI, cloud</li>
                <li className="flex items-start gap-2.5"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#F97316] shrink-0" /> Trusted by clients across 4 continents</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 lg:py-20 border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// what we believe</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3 max-w-2xl">Four principles that shape every release.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {PILLARS.map(({ Icon, title, desc, color }) => (
              <div key={title} className="rounded-[6px] bg-white border border-[#E2E8F0] p-6 hover:border-[#030712] transition-colors">
                <div className="w-12 h-12 rounded-[6px] flex items-center justify-center" style={{ background: `${color}14`, color }}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-lg mt-5">{title}</h3>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-16 lg:py-20 border-b border-[#E2E8F0] bg-[#0F1115] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <span className="text-xs font-mono uppercase tracking-widest text-white/50">// by the numbers</span>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-10">
            {NUMBERS.map((n) => (
              <div key={n.label}>
                <div className="font-display font-extrabold text-5xl md:text-6xl bg-gradient-to-r from-[#0044FF] via-[#7C3AED] to-[#EC4899] bg-clip-text text-transparent">
                  {n.value}
                </div>
                <div className="text-sm text-white/70 mt-3">{n.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-20 border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <span className="text-xs font-mono uppercase tracking-widest text-[#4B5563]">// values</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3 max-w-2xl">How we work.</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            {VALUES.map((v) => (
              <div key={v.tag} className="rounded-[6px] bg-white border border-[#E2E8F0] p-6">
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#0044FF]">{v.tag}</span>
                <p className="text-base text-[#0F1115] mt-3 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl leading-[1.05]">Ready to put HelloSir on your site?</h2>
            <p className="text-[#4B5563] mt-3">Free trial. No credit card. Live in minutes.</p>
          </div>
          <Link to="/signup" className="btn-primary text-base" data-testid="about-cta">Start free</Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
